/**
 * Consent state — single source of truth for whether nonessential trackers may run.
 *
 * Design notes:
 * - Stored in a first-party cookie (not localStorage) so the backend can read the
 *   same decision on /api/meta/capi. Browser-side and server-side tracking must
 *   agree, otherwise "reject" in the browser just moves the transmission to the server.
 * - Categories are deliberately few: necessary (always on, not stored), analytics, advertising.
 * - GPC (Global Privacy Control) is treated as a standing opt-out that the UI cannot
 *   override. See hasGPC() below.
 */

import Cookies from "js-cookie";

export const CONSENT_COOKIE = "tbb_consent";
const CONSENT_VERSION = 1;
const CONSENT_MAX_AGE_DAYS = 180;

// The pre-existing "cookieConsent" cookie was set by a banner that was never
// actually rendered (the component was orphaned), and it recorded only a single
// undifferentiated "ok" with no category granularity. It is not a valid record of
// a choice, so we ignore it and start clean under a new key.
const LEGACY_COOKIE = "cookieConsent";

/** Categories a visitor can decide on. "necessary" is always granted and never stored. */
export const CATEGORIES = ["analytics", "advertising"];

/**
 * True when the browser is sending a Global Privacy Control signal.
 *
 * Strictly, GPC is an opt-out of *sale/sharing* for cross-context behavioral
 * advertising, which maps to the advertising category. We also deny analytics
 * because GA4 can itself constitute "sharing" when Google Signals or Google Ads
 * linking is enabled on the property — a console-side setting this code cannot
 * inspect. Denying both is the conservative reading. If Google Signals is
 * confirmed off, `gpcDeniedCategories` can safely be narrowed to advertising only.
 */
export function hasGPC() {
  if (typeof navigator === "undefined") return false;
  return navigator.globalPrivacyControl === true;
}

export const gpcDeniedCategories = ["analytics", "advertising"];

function emptyConsent() {
  return { analytics: false, advertising: false };
}

/**
 * The visitor's stored decision, or null if they have not decided yet.
 * Never throws — a malformed or absent cookie reads as "undecided".
 */
export function getStoredConsent() {
  let raw;
  try {
    raw = Cookies.get(CONSENT_COOKIE);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== CONSENT_VERSION) return null;
    return {
      analytics: parsed.analytics === true,
      advertising: parsed.advertising === true,
    };
  } catch {
    return null;
  }
}

/**
 * Effective consent, i.e. what the trackers should actually obey right now.
 * GPC overrides a stored grant — a visitor cannot be tracked into a category
 * their browser has already opted them out of.
 */
export function getEffectiveConsent() {
  const stored = getStoredConsent();
  const base = stored || emptyConsent();

  if (hasGPC()) {
    const forced = { ...base };
    for (const category of gpcDeniedCategories) forced[category] = false;
    return forced;
  }

  return base;
}

/** True once the visitor has made an explicit choice (so the banner can hide). */
export function hasDecided() {
  return getStoredConsent() !== null;
}

/**
 * Persist a decision. Returns the effective consent after GPC is applied.
 * Written as a first-party cookie so the backend sees the same value.
 */
export function setStoredConsent({ analytics, advertising }) {
  const value = {
    v: CONSENT_VERSION,
    analytics: analytics === true,
    advertising: advertising === true,
    gpc: hasGPC(),
    ts: new Date().toISOString(),
  };

  try {
    Cookies.set(CONSENT_COOKIE, JSON.stringify(value), {
      expires: CONSENT_MAX_AGE_DAYS,
      sameSite: "Lax",
      path: "/",
      // Only mark Secure on https so local http dev still works.
      secure:
        typeof window !== "undefined" && window.location.protocol === "https:",
    });
  } catch {
    // Storage blocked (private mode, blocked cookies). Nothing is persisted, which
    // means the visitor stays "undecided" and trackers stay off. Failing closed is correct.
  }

  return getEffectiveConsent();
}

/** Clear our own consent record and the dead legacy cookie. */
export function clearStoredConsent() {
  try {
    Cookies.remove(CONSENT_COOKIE, { path: "/" });
    Cookies.remove(LEGACY_COOKIE, { path: "/" });
  } catch {
    // no-op
  }
}

/**
 * Best-effort removal of cookies dropped by trackers we are switching off.
 * Scripts already loaded into the page cannot be unloaded, so callers that
 * downgrade consent should reload afterwards; this just stops the identifiers
 * from surviving that reload.
 */
export function clearTrackerCookies() {
  if (typeof document === "undefined") return;

  const names = new Set(["_fbp", "_fbc"]);
  // GA writes _ga plus one _ga_<STREAM_ID> per property.
  for (const pair of document.cookie.split(";")) {
    const name = pair.split("=")[0]?.trim();
    if (!name) continue;
    if (name === "_ga" || name.startsWith("_ga_") || name.startsWith("_gid")) {
      names.add(name);
    }
  }

  const host = window.location.hostname;
  // A cookie must be removed with the same domain/path it was set on, and we do
  // not know which the vendor used, so clear the plausible combinations.
  const domains = [undefined, host, `.${host}`];
  const bareDomain = host.split(".").slice(-2).join(".");
  if (bareDomain && bareDomain !== host) domains.push(`.${bareDomain}`);

  for (const name of names) {
    for (const domain of domains) {
      try {
        Cookies.remove(name, { path: "/", ...(domain ? { domain } : {}) });
      } catch {
        // no-op
      }
    }
  }
}

/** Header value the client sends so the backend can double-check the decision. */
export function consentHeaders() {
  const consent = getEffectiveConsent();
  return {
    "X-TBB-Consent-Advertising": consent.advertising ? "1" : "0",
  };
}
